import { useState, useRef, useEffect } from "react";
import { Badge, Modal, Form } from "antd";
import BellIcon from "@Assets/icons/notification.svg";
import UserAvatar from "@Assets/images/userProfile.jpg";
import ArrowDownIcon from "@Assets/icons/arrow-select.svg";
import useAuthStore from "@Store/authStore";
import { useNavigate } from "react-router-dom";
import { NavigationRoutes } from "@Navigation/NavigationRoutes";
import LogoutModalIcon from "@Assets/icons/logout-icon.svg";
import ConfirmationModal from "@Components/Modal/ConfirmationModal";
import BackIcon from "@Assets/icons/arrow-back.svg";
import NotificationContainer from "../Notifications/NotificationContainer";
import { useUserProfile } from "@Pages/AppScreens/UserManagement/Queries";
import { getInitials } from "@Utils/index";

type AppHeaderProps = {
  moduleTitle?: string;
};

const AppHeader = ({ moduleTitle = "" }: AppHeaderProps) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const { removeUserAuthentication } = useAuthStore();


  const handleLogout = () => {
    removeUserAuthentication();
    navigate(NavigationRoutes.AUTH_ROUTES.LOGIN);
    setShowLogoutModal(false);
  };

  const { data: profileDetails, isLoading: isProfileLoading } =
    useUserProfile();
  const user = profileDetails?.body?.user;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const name = `${user?.firstName} ${user?.lastName}`;

  return (
    <div className="flex items-center justify-between w-full gap-4 px-2">
      <div className="flex items-center min-w-0">
        <h1 className="!text-lg font-Nunito font-semibold text-textTitle truncate flex !items-center gap-2">
          {moduleTitle === "User Details" && (
            <button>
              <BackIcon
                className="cursor-pointer"
                onClick={() => navigate(-1)}
              />
            </button>
          )}{" "}
          {moduleTitle}
        </h1>
      </div>

      <div className="flex !items-center gap-3">
        <div
          className="flex !items-center !bg-bgIcons rounded-full px-4 py-3 cursor-pointer "
          style={{ background: "#F7F7F7" }}
        >
          <Badge
            count={4}
            size="small"
            offset={[0, 0]}
            className="!text-app-primary"
          >
            <BellIcon />
          </Badge>
        </div>

        {isProfileLoading ? (
          <div className="flex items-center gap-3 animate-pulse">
            <div className="w-[2.5rem] h-[2.5rem] rounded-full bg-gray-200" />
            <div className="flex flex-col gap-1">
              <span className="h-4 w-28 rounded bg-gray-200" />
              <span className="h-3 w-16 rounded bg-gray-100" />
            </div>
            <div className="w-3 h-3 rounded bg-gray-200" />
          </div>
        ) : (
          <>
            {user?.avatar ? (
              <div>
                <img
                  src={user?.avatar || UserAvatar}
                  className="w-[2.5rem] h-[2.5rem] rounded-full object-cover"
                  alt="User Avatar"
                />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#859F43] flex items-center justify-center text-white text-base font-semibold">
                {getInitials(name)}
              </div>
            )}

            <div className="flex flex-col">
              <div className="flex justify-between items-center gap-2">
                <span className="font-semibold  capitalize !text-app-primary !text-base leading-normal font-Nunito">
                  {user?.firstName} {user?.lastName}
                </span>
                <div
                  className="cursor-pointer"
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  <ArrowDownIcon className="w-3 h-2" />
                </div>
              </div>
              <span className="font-semibold !text-secondaryGray !text-sm leading-normal font-Nunito">
                Admin
              </span>
            </div>

            <div className="relative" ref={dropdownRef}>
              {/* Dropdown Menu */}
              {showDropdown && (
                <div className="absolute right-0 mt-7 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-0">
                    <p
                      onClick={() =>
                        navigate(
                          NavigationRoutes.DASHBOARD_ROUTES.PROFILE_MANAGEMENT,
                        )
                      }
                      className="w-full cursor-pointer text-left px-4 py-4 text-sm !text-textTitle hover:bg-[#FEF6F6] transition-colors"
                    >
                      My Profile
                    </p>
                    <p
                      onClick={() => {
                        setShowLogoutModal(true);
                        setShowDropdown(false);
                      }}
                      className="w-full cursor-pointer text-left px-4 py-4 text-sm !text-[#EF4444] hover:bg-[#FEF6F6] transition-colors"
                    >
                      Logout
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmationModal
        open={showLogoutModal}
        title="Logout"
        content="Are you sure you want to logout?"
        icon={
          <span className="w-24 h-24 inline-flex items-center justify-center ">
            <LogoutModalIcon className="w-20 h-20" />
          </span>
        }
        onCancel={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        actionButtons={[
          {
            label: "Cancel",
            onClick: () => setShowLogoutModal(false),
            variant: "cancel",
          },
          {
            label: "Confirm",
            onClick: handleLogout,
            variant: "danger",
          },
        ]}
      />
    </div>
  );
};

export default AppHeader;
